import {
  Link as RouterLink,
  Navigate,
  useLocation,
  useNavigate,
  useParams,
  type NavigateOptions,
  type To,
} from 'react-router-dom';
import { forwardRef, type ComponentProps } from 'react';

export const routing = {
  locales: ['en'] as const,
  defaultLocale: 'en',
  localePrefix: 'as-needed' as const,
  localeDetection: false,
};

type LinkProps = Omit<ComponentProps<typeof RouterLink>, 'to'> & {
  href?: To;
  to?: To;
  locale?: string;
};

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link(
  { href, locale: _locale, to, ...props },
  ref
) {
  const destination = href ?? to ?? '/';
  return <RouterLink ref={ref} to={destination} {...props} />;
});

export function redirect(href: string) {
  return <Navigate to={href} replace />;
}

export function usePathname() {
  return useLocation().pathname;
}

export function useRouter() {
  const navigate = useNavigate();
  return {
    push: (href: string, options?: NavigateOptions) => navigate(href, options),
    replace: (href: string, options?: NavigateOptions) =>
      navigate(href, { ...options, replace: true }),
    back: () => navigate(-1),
    refresh: () => {
      if (typeof window !== 'undefined') window.location.reload();
    },
  };
}

export function getPathname({ href }: { href: string; locale?: string }) {
  return href;
}

export { useParams, useLocation, useNavigate };
